import { useCallback, useMemo, useState } from 'react';
import { Button } from 'tuui';
import { Radio } from 'taboola-ultimate-ui';
import { RadioGroupField } from 'modules/campaigns/components/Form';
import OutlineEditableText from 'modules/campaigns/components/OutlineEditableText';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage, useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { STEP_IDS } from '../../config/stepIds';
import { NewEventHeader } from '../Headers/NewEventHeader';
import styles from './globalButtonConversionSetup.module.scss';

const SETUP_TYPE = {
    COMBINE_SAME_TEXT_BUTTONS: 'COMBINE_SAME_TEXT_BUTTONS',
    COMBINE_BUTTONS_THAT_CONTAINS_URL: 'COMBINE_BUTTONS_THAT_CONTAINS_URL',
};

export const GlobalButtonConversionSetup = ({
    navigateToStepId,
    handleCancelWizard,
    updateDraftEvent,
    currentConversion,
}) => {
    const [setupType, setSetupType] = useState(SETUP_TYPE.COMBINE_SAME_TEXT_BUTTONS);
    const [buttonText, setButtonText] = useState(currentConversion.displayText ?? '');
    const [buttonHref, setButtonHref] = useState(currentConversion.href ?? '');
    const { formatMessage } = useIntl();

    const onRadioChange = useCallback(
        value => {
            setSetupType(value);
            setButtonText('');
            setButtonHref('');
            updateDraftEvent(draft => {
                const existingElement = draft.trackedElements?.[0] || {};
                return {
                    ...draft,
                    trackedElements: [
                        {
                            ...existingElement,
                            displayText: '',
                            href: '',
                        },
                    ],
                };
            });
        },
        [updateDraftEvent]
    );

    const handleButtonTextChange = useCallback(
        value => {
            setButtonText(value);
            updateDraftEvent(draft => {
                const existingElement = draft.trackedElements?.[0] || {};
                return {
                    ...draft,
                    trackedElements: [
                        {
                            ...existingElement,
                            displayText: value,
                        },
                    ],
                };
            });
        },
        [updateDraftEvent]
    );

    const handleButtonHrefChange = useCallback(
        value => {
            setButtonHref(value);
            updateDraftEvent(draft => {
                const existingElement = draft.trackedElements?.[0] || {};
                return {
                    ...draft,
                    trackedElements: [
                        {
                            ...existingElement,
                            href: value,
                        },
                    ],
                };
            });
        },
        [updateDraftEvent]
    );

    const navigateToPreviousStep = () => navigateToStepId(STEP_IDS.BUTTON_CONVERSION_LOCATION_SELECTOR);
    const navigateToNextStep = () => {
        navigateToStepId(STEP_IDS.SETUP_CLICKS_EVENT_CATEGORY);
    };

    const optionsMap = useMemo(
        () => ({
            [SETUP_TYPE.COMBINE_SAME_TEXT_BUTTONS]: {
                title: (
                    <FormattedMessage
                        id="codeless.conversion.type.button.location.cross_site.same_text.title"
                        defaultMessage="Combine buttons with same text"
                    />
                ),
                placeholder: formatMessage({
                    id: 'codeless.conversion.type.button.location.cross_site.same_text.input.placeholder',
                    defaultMessage: 'Enter button text',
                }),
                value: buttonText,
                onChange: handleButtonTextChange,
                isDisabled: setupType !== SETUP_TYPE.COMBINE_SAME_TEXT_BUTTONS,
            },
            [SETUP_TYPE.COMBINE_BUTTONS_THAT_CONTAINS_URL]: {
                title: (
                    <FormattedMessage
                        id="codeless.conversion.type.button.location.cross_site.contains_url.title"
                        defaultMessage="Combine buttons that contains URL"
                    />
                ),
                value: buttonHref,
                placeholder: formatMessage({
                    id: 'codeless.conversion.type.button.location.cross_site.contains_url.input.placeholder',
                    defaultMessage: 'Enter URL',
                }),
                onChange: handleButtonHrefChange,
                isDisabled: setupType !== SETUP_TYPE.COMBINE_BUTTONS_THAT_CONTAINS_URL,
            },
        }),
        [formatMessage, buttonText, setupType, buttonHref, handleButtonTextChange, handleButtonHrefChange]
    );

    const isNextDisabled = setupType === SETUP_TYPE.COMBINE_SAME_TEXT_BUTTONS ? !buttonText : !buttonHref;

    return (
        <div className={styles['container']}>
            <NewEventHeader handlerArrowLeftButton={navigateToPreviousStep} />
            <RadioGroupField
                groupTitle={
                    <FormattedMessage
                        id="codeless.conversion.global.setup.header"
                        defaultMessage="Setup global buttons"
                    />
                }
                radioGroupName="conversionLocationSelection"
                onChange={onRadioChange}
                selectedValue={setupType}
                data-metrics-component="button-conversion-global-setup"
                className={styles['group']}
            >
                {Object.entries(optionsMap).map(([optionValue, optionData]) => (
                    <div key={`button-conversion-global-setup-${optionValue}`} className={styles['radio-container']}>
                        <Radio
                            labelClassName={styles['radio']}
                            descriptionClassName={styles['description']}
                            aria-label={`${optionValue}`}
                            value={optionValue}
                            title={optionData.title}
                            description={optionData.description}
                            data-metrics-event-name={GTM_EVENTS.USABILITY}
                            data-metrics-value={optionValue}
                            inputClassName={styles['input']}
                        />
                        <div className={styles['input-container']}>
                            <OutlineEditableText
                                placeholder={optionData.placeholder}
                                disabled={optionData.isDisabled}
                                value={optionData.value}
                                onChange={optionData.onChange}
                                className={styles['editable-text']}
                                data-metrics-event-name={GTM_EVENTS.USABILITY}
                                data-metrics-component={`${optionValue}-input`}
                            />
                        </div>
                    </div>
                ))}
            </RadioGroupField>
            <footer className={styles['footer']}>
                <Button
                    onClick={handleCancelWizard}
                    variant={Button.variant.outline}
                    size={Button.size.md}
                    aria-label="Cancel"
                >
                    <FormattedMessage id="codeless.cancel.button" defaultMessage="Cancel" />
                </Button>
                <Button
                    onClick={navigateToNextStep}
                    variant={Button.variant.primary}
                    size={Button.size.md}
                    className={styles['next-button']}
                    aria-label="Next"
                    disabled={isNextDisabled}
                >
                    <FormattedMessage id="codeless.next.button" defaultMessage="Next" />
                </Button>
            </footer>
        </div>
    );
};
